import React, { useEffect, useMemo, useState } from "react";
import { Loader2, Check, AlertCircle, Lock } from "lucide-react";
import { getCreditRules, updateCreditRule } from "../../services/api";

function RuleRow({ rule, onSaved }) {
  const [amount, setAmount] = useState(rule.amount);
  const [isActive, setIsActive] = useState(rule.isActive);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [justSaved, setJustSaved] = useState(false);

  const dirty = amount !== rule.amount || isActive !== rule.isActive;

  const handleSave = async () => {
    if (!Number.isInteger(Number(amount))) {
      setError("Amount must be a whole number");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const { rule: updated } = await updateCreditRule(rule.code, {
        amount: Number(amount),
        isActive,
      });
      onSaved(updated);
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 1500);
    } catch (err) {
      setError(err.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (!rule.configurable) {
    return (
      <div className="flex items-center justify-between gap-4 py-4 border-b border-gray-100 last:border-0 opacity-50">
        <div className="pr-4">
          <p className="font-semibold text-gray-900 flex items-center gap-1.5">
            <Lock className="w-3.5 h-3.5" /> {rule.label}
          </p>
          <p className="text-xs text-gray-500 mt-0.5 font-mono">{rule.code}</p>
          <p className="text-sm text-gray-500 mt-1">Not awarded by any code path — not configurable.</p>
        </div>
        <div className="text-sm text-gray-500 whitespace-nowrap">{rule.amount} pts</div>
      </div>
    );
  }

  return (
    <div className="flex items-start justify-between gap-4 py-4 border-b border-gray-100 last:border-0">
      <div className="pr-4 min-w-0">
        <p className="font-semibold text-gray-900">{rule.label}</p>
        <p className="text-xs text-gray-500 mt-0.5 font-mono">{rule.code}</p>
        {rule.description && (
          <p className="text-sm text-gray-500 mt-1">{rule.description}</p>
        )}
        {error && (
          <p className="text-sm text-red-600 mt-1.5 flex items-center gap-1">
            <AlertCircle className="w-3.5 h-3.5" /> {error}
          </p>
        )}
      </div>

      <div className="flex items-center gap-3 shrink-0">
        <label className="flex items-center gap-1.5 text-sm text-gray-600">
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
            className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
          />
          Active
        </label>

        <input
          type="number"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          className="w-24 rounded-lg border border-gray-300 px-2.5 py-1.5 text-sm text-right focus:outline-none focus:ring-2 focus:ring-teal-500"
        />

        <button
          type="button"
          onClick={handleSave}
          disabled={!dirty || saving}
          className="inline-flex items-center gap-1.5 rounded-lg bg-teal-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-teal-700 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
        >
          {saving ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : justSaved ? (
            <Check className="w-3.5 h-3.5" />
          ) : null}
          Save
        </button>
      </div>
    </div>
  );
}

export default function CreditRules() {
  const [rules, setRules] = useState(null);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    getCreditRules()
      .then((data) => setRules(data.rules))
      .catch((err) => setLoadError(err.message || "Failed to load credit rules"));
  }, []);

  const grouped = useMemo(() => {
    if (!rules) return [];
    const byCategory = new Map();
    for (const rule of rules) {
      const key = rule.category || "Other";
      if (!byCategory.has(key)) byCategory.set(key, []);
      byCategory.get(key).push(rule);
    }
    return Array.from(byCategory.entries());
  }, [rules]);

  const handleSaved = (updated) => {
    setRules((prev) =>
      prev.map((r) => (r.code === updated.code ? { ...updated, configurable: r.configurable } : r))
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-2xl font-bold text-gray-900">Credit Rules</h1>
        <p className="text-gray-500 text-sm mt-1 mb-6">
          RoamPoints reward amounts. Changes apply immediately to future awards — they do not
          affect credits already granted.
        </p>

        {loadError && (
          <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 mb-6">
            {loadError}
          </div>
        )}

        {!rules && !loadError && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-teal-600" />
          </div>
        )}

        {grouped.map(([category, categoryRules]) => (
          <div key={category} className="bg-white rounded-xl border border-gray-200 p-5 mb-5">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-400 mb-2">
              {category}
            </h2>
            {categoryRules.map((rule) => (
              <RuleRow key={rule.code} rule={rule} onSaved={handleSaved} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
